import { ApiError, CartoApiError, RequestStatus } from 'features/common'
import { Connection, ProviderType } from 'features/connections'
import {
  DataExplorerTable,
  DataExplorerResource,
  DataExplorerTreeItem,
  TilesetRequest,
  DataTreeResourceRequestProps,
  TreeObjectType,
  TablePreviewOpts,
  DataTablePreview,
  Tab,
  DataTreeItemTypes,
  DataExplorerTreeItemChildren,
  DataTreeSubscriptionInfoRequestProps,
  DataExplorerDataSet
} from '../types'
import { createSelector, createAction, createSlice, PayloadAction } from '@reduxjs/toolkit'
import merge from 'deepmerge'
import { Tileset, TilesetSizeByZoom } from 'features/data-explorer/types'
import { Raster } from 'features/data-explorer/types'
import { setNestedPropertyFrom } from 'features/common/utils/objects'
import { RootState } from 'app/state/store'
import { get } from 'lodash'
import { isApiError } from 'features/common/api/apiError'
import { isResource } from '../ui/DataTree/dataTreeCommons'

export const name = 'data_explorer'
export const DATA_TREE_OBJECT_INFO_FETCH_REQUEST = `${name}/fetch_tree_object_info_request`
export const DATA_TREE_RESOURCE_FETCH_REQUEST = `${name}/fetch_data_tree_resource_request`
export const DATA_TREE_RESOURCE_BY_LINK_FETCH_REQUEST = `${name}/fetch_data_tree_resource_by_link_request`
export const DATA_TREE_TILESET_STATS_FETCH_REQUEST = `${name}/fetch_tileset_stats_request`
export const DATA_TREE_RESOURCES_FROM_PATH_REQUEST = `${name}/fetch_resources_from_path`
export const DATA_TABLE_PREVIEW_FETCH_REQUEST = `${name}/fetch_table_preview_request`
export const GET_SUBSCRIPTION_INFO_REQUEST = `${name}/get_subscription_info_request`
export const GET_SUBSCRIPTION_TABLE_REQUEST = `${name}/get_subscription_table_request`
export const CHECK_WRITE_PERMISSIONS_REQUEST = `${name}/check_write_permissions_request`
export const DELETE_RESOURCE_REQUEST = `${name}/delete_resource_request`
export const RELOAD_DATA_TREE_NODE = `${name}/reload_data_tree_node`
export const GET_SUBSCRIPTION_FOR_ENRICH_REQUEST = `${name}/get_subscription_for_enrich_request`
export const GET_SUBSCRIPTION_TABLE_ENRICH_REQUEST = `${name}/get_subscription_table_enrich_request`
export const RESET_TABLE_ENRICH_REQUEST = `${name}/reset_table_enrich_request`
export const CHECK_TABLE_RESOURCE_STATS_REQUEST = `${name}/check_table_resource_stats_request`

function attachDataTreeObject<T>(payload: { targetKey: string; data: T }) {
  const newTreeItem: Record<string, T> = {}
  payload.targetKey && setNestedPropertyFrom(newTreeItem, payload.targetKey, payload.data)
  return newTreeItem
}

function handleRefreshByReplacing({
  currentTreeObject,
  newAttachedObject,
  targetKey
}: {
  currentTreeObject: DataExplorerTree
  newAttachedObject: Record<string, Record<string, DataExplorerResource> | ApiError>
  targetKey: string
}) {
  const objectPath = targetKey.split('.').slice(0, -1).join('.')
  const oldObject = get(currentTreeObject, objectPath) as DataExplorerTreeItemChildren
  const newObject = get(newAttachedObject, objectPath) as DataExplorerTreeItemChildren
  oldObject.children = newObject.children
  oldObject.totalChildren = newObject.totalChildren
}

export type DataExplorerTree = Record<string, DataExplorerTreeItemChildren | ApiError>
export interface DataExplorerState {
  expandedTree: Tab
  expandedNodes: string[]
  currentSelectedNode: string
  currentSelectedTreeItemId: string
  currentConnectionId: string
  checkWritePermissionStatus: RequestStatus
  checkWritePermissionError?: ApiError
  deleteResourceStatus: RequestStatus
  deleteResourceError?: ApiError | null
  dataExplorerTree: DataExplorerTree
  dataExplorerTreeStatus: RequestStatus
  dataExplorerLoadingNextPage?: boolean
  dataExplorerSearching: boolean
  dataExplorerTreeError?: CartoApiError | null
  dataExplorerNodeNotFound?: string | null
  dataExplorerTreeLoadingNodes: string[]
  dataExplorerFolderView: 'table' | 'grid'
  currentRaster: Raster | null
  currentRasterStatus: RequestStatus
  currentRasterError?: CartoApiError | null
  currentTileset: Tileset | null
  currentTilesetStatus: RequestStatus
  currentTilesetError?: CartoApiError | null
  currentTilesetStats?: TilesetSizeByZoom[] | null
  currentTilesetStatsStatus: RequestStatus
  currentTilesetStatsError?: ApiError | null
  currentTable: DataExplorerTable | null
  currentTableStatus: RequestStatus
  currentTableError?: CartoApiError | null
  currentTableResourceStatsStatus: RequestStatus
  currentTableDataPreview?: DataTablePreview | null
  currentTableDataPreviewStatus: RequestStatus
  currentTableDataPreviewError?: ApiError | null
  currentTableForEnrich: DataExplorerTable | null
  currentTableForEnrichStatus: RequestStatus
  dataExplorerSearchNotFound: boolean
  dataExplorerDatasets: DataExplorerDataSet[]
}

const initialState: DataExplorerState = {
  expandedTree: Tab.EXPLORER,
  currentConnectionId: '',
  currentSelectedNode: '',
  currentSelectedTreeItemId: '',
  expandedNodes: [],
  checkWritePermissionStatus: RequestStatus.Idle,
  deleteResourceStatus: RequestStatus.Idle,
  dataExplorerDatasets: [],
  // REFACTOR PENDING
  dataExplorerTree: {
    bigquery: {},
    snowflake: {},
    redshift: {},
    postgres: {},
    databricks: {},
    databricksRest: {}
  },
  dataExplorerTreeStatus: RequestStatus.Idle,
  dataExplorerTreeLoadingNodes: [],
  dataExplorerFolderView: 'table',
  dataExplorerSearching: false,
  dataExplorerSearchNotFound: false,
  dataExplorerLoadingNextPage: false,
  currentRaster: null,
  currentRasterStatus: RequestStatus.Idle,
  currentTileset: null,
  currentTilesetStatus: RequestStatus.Idle,
  currentTilesetStatsStatus: RequestStatus.Idle,
  currentTableDataPreview: null,
  currentTableDataPreviewStatus: RequestStatus.Idle,
  currentTable: null,
  currentTableStatus: RequestStatus.Idle,
  currentTableResourceStatsStatus: RequestStatus.Idle,
  currentTableForEnrich: null,
  currentTableForEnrichStatus: RequestStatus.Idle
}

const asyncActions = {
  fetchResourcesFromPath: createAction<{
    connectionId: string
    provider: string
    resourcePath: string
  }>(DATA_TREE_RESOURCES_FROM_PATH_REQUEST),
  fetchTablePreviewRequest: createAction<TablePreviewOpts>(DATA_TABLE_PREVIEW_FETCH_REQUEST),
  fetchDataTreeResourceRequest: createAction<DataTreeResourceRequestProps>(DATA_TREE_RESOURCE_FETCH_REQUEST),
  fetchDataTreeResourceByLink: createAction<{
    link: string
    targetKey: string
    onlyGeomData?: boolean
    page?: number
    search?: string
    persistSearch?: boolean
    refresh?: boolean
    connectionId?: string
  }>(DATA_TREE_RESOURCE_BY_LINK_FETCH_REQUEST),
  fetchDataExplorerObjectRequest: createAction<{
    link: string
    object?: TreeObjectType
    fetchDataPreview?: boolean
    connectionName?: string
  }>(DATA_TREE_OBJECT_INFO_FETCH_REQUEST),
  fetchTilesetStatsRequest: createAction<TilesetRequest>(DATA_TREE_TILESET_STATS_FETCH_REQUEST),
  getTableStatsRequest: createAction<DataTreeResourceRequestProps>(GET_SUBSCRIPTION_TABLE_REQUEST),
  getSubscriptionInfoRequest: createAction<DataTreeSubscriptionInfoRequestProps>(GET_SUBSCRIPTION_INFO_REQUEST),
  deleteResourceRequest: createAction<{ connectionName: string; connectionId: string; path: string }>(
    DELETE_RESOURCE_REQUEST
  ),
  reloadDataTreeItemRequest: createAction<{
    connectionId: Connection['id']
    provider_id: Connection['provider_id']
    items: Array<DataExplorerTreeItem['id']>
  }>(RELOAD_DATA_TREE_NODE),
  getSubscriptionForEnrichRequest: createAction<DataTreeSubscriptionInfoRequestProps>(
    GET_SUBSCRIPTION_FOR_ENRICH_REQUEST
  ),
  getTableStatsForEnrichRequest: createAction<DataTreeResourceRequestProps>(GET_SUBSCRIPTION_TABLE_ENRICH_REQUEST),
  resetTableForEnrichRequest: createAction<void>(RESET_TABLE_ENRICH_REQUEST),
  checkTableResourceStatsRequest: createAction<{ table: DataExplorerTable; connection: Connection }>(
    CHECK_TABLE_RESOURCE_STATS_REQUEST
  )
}

const reducers = {
  setExpandedTree: (state: DataExplorerState, action: PayloadAction<Tab>) => {
    state.expandedTree = action.payload
  },
  updateDataExplorerTreeConnection: (
    state: DataExplorerState,
    action: PayloadAction<Partial<Connection & DataExplorerTreeItem>>
  ) => {
    const { id: connectionId, provider_id: _providerId, viewerCredentialsAttached } = action.payload
    if (!connectionId) {
      return
    }

    const providerId =
      _providerId ||
      Object.values(ProviderType).find((providerId) => state.dataExplorerTree[providerId]?.[connectionId])
    if (!providerId) {
      return
    }
    if (!state.dataExplorerTree[providerId]) {
      state.dataExplorerTree[providerId] = {}
    }

    const oldNode = state.dataExplorerTree[providerId][connectionId]
    let update = action.payload
    if (oldNode && oldNode.viewerCredentialsAttached === true && viewerCredentialsAttached === false) {
      // if we just removed credentials, we should forget already-loaded children of this connection
      update = {
        ...update,
        children: undefined
      }
    }

    const newNode = {
      type: DataTreeItemTypes.Connection,
      children: undefined,
      ...oldNode,
      ...update
    }
    state.dataExplorerTree[providerId][connectionId] = newNode
  },
  setDataExplorerTree: (
    state: DataExplorerState,
    action: PayloadAction<{
      targetKey: string
      data: Record<string, DataExplorerResource> | ApiError
      totalChildren?: number
      page?: number
      isEmpty?: boolean
      unknownTotal?: boolean
      refresh?: boolean
    }>
  ) => {
    const dataExplorerTree = state.dataExplorerTree
    const attachedObject = attachDataTreeObject(action.payload)
    if (action.payload.totalChildren || action.payload.totalChildren === 0) {
      const objectPath = action.payload.targetKey.split('.').slice(0, -1).join('.')
      const object = get(attachedObject, objectPath) as unknown as DataExplorerTreeItem
      object.totalChildren = action.payload.totalChildren
      object.unknownTotal = action.payload.unknownTotal
      if (action.payload.totalChildren !== 0) {
        object.page = action.payload.page as number
      }
    }
    if (action.payload.refresh) {
      handleRefreshByReplacing({
        currentTreeObject: dataExplorerTree,
        newAttachedObject: attachedObject,
        targetKey: action.payload.targetKey
      })
    }
    state.dataExplorerTree = merge(dataExplorerTree, attachedObject)
  },
  setDataExplorerTreeStatus: (state: DataExplorerState, action: PayloadAction<RequestStatus>) => {
    state.dataExplorerTreeStatus = action.payload
  },
  setDataExplorerTreeLoadingNode: (state: DataExplorerState, action: PayloadAction<string>) => {
    state.dataExplorerTreeLoadingNodes.push(action.payload)
  },
  setDataExplorerLoadingNextPage: (state: DataExplorerState, action: PayloadAction<boolean>) => {
    state.dataExplorerLoadingNextPage = action.payload
  },
  deleteDataExplorerTreeLoadingNode: (state: DataExplorerState, action: PayloadAction<string>) => {
    state.dataExplorerTreeLoadingNodes = state.dataExplorerTreeLoadingNodes.filter(
      (node: string) => node !== action.payload
    )
  },
  clearDataExplorerTreeLoadingErrors: (state: DataExplorerState, action: PayloadAction<string>) => {
    const current = get(state.dataExplorerTree, action.payload)
    if (isApiError((current as DataExplorerTreeItemChildren)?.children)) {
      state.dataExplorerTree = merge(
        state.dataExplorerTree,
        attachDataTreeObject({ targetKey: action.payload, data: undefined })
      )
    }
    state.dataExplorerTreeError = null
  },
  setDataExplorerTreeError: (state: DataExplorerState, action: PayloadAction<CartoApiError | null>) => {
    state.dataExplorerTreeError = action.payload
  },
  setDataExplorerNodeNotFound: (state: DataExplorerState, action: PayloadAction<string | null>) => {
    state.dataExplorerNodeNotFound = action.payload
  },
  setCurrentRaster: (state: DataExplorerState, action: PayloadAction<Raster | null>) => {
    state.currentTable = null
    state.currentTileset = null
    state.currentRaster = action.payload
  },
  setCurrentRasterStatus: (state: DataExplorerState, action: PayloadAction<RequestStatus>) => {
    state.currentRasterStatus = action.payload
  },
  setCurrentRasterError: (state: DataExplorerState, action: PayloadAction<CartoApiError | null>) => {
    state.currentRasterError = action.payload
  },
  setCurrentTileset: (state: DataExplorerState, action: PayloadAction<Tileset | null>) => {
    state.currentTable = null
    state.currentTileset = action.payload
    state.currentRaster = null
  },
  setCurrentTilesetStatus: (state: DataExplorerState, action: PayloadAction<RequestStatus>) => {
    state.currentTilesetStatus = action.payload
  },
  setCurrentTilesetError: (state: DataExplorerState, action: PayloadAction<CartoApiError | null>) => {
    state.currentTilesetError = action.payload
  },
  setCurrentTilesetStats: (state: DataExplorerState, action: PayloadAction<TilesetSizeByZoom[] | null>) => {
    state.currentTilesetStats = action.payload
  },
  setCurrentTilesetStatsStatus: (state: DataExplorerState, action: PayloadAction<RequestStatus>) => {
    state.currentTilesetStatsStatus = action.payload
  },
  setCurrentTilesetStatsError: (state: DataExplorerState, action: PayloadAction<ApiError | null>) => {
    state.currentTilesetStatsError = action.payload
  },
  setCurrentTable: (state: DataExplorerState, action: PayloadAction<DataExplorerTable | null>) => {
    state.currentTileset = null
    state.currentTable = action.payload
    state.currentRaster = null
  },
  setCurrentTableStatus: (state: DataExplorerState, action: PayloadAction<RequestStatus>) => {
    state.currentTableStatus = action.payload
  },
  setCurrentTableError: (state: DataExplorerState, action: PayloadAction<CartoApiError | null>) => {
    state.currentTableError = action.payload
  },
  setCurrentTableResourceStatsStatus: (state: DataExplorerState, action: PayloadAction<RequestStatus>) => {
    state.currentTableResourceStatsStatus = action.payload
  },
  setDeleteConnectionFromDataTree: (state: DataExplorerState, action: PayloadAction<string>) => {
    Object.keys(state.dataExplorerTree).map((providerKey: string) => {
      const connectionIndex = Object.keys(state.dataExplorerTree[providerKey]).indexOf(action.payload)
      if (connectionIndex !== -1) {
        delete state.dataExplorerTree[providerKey][action.payload]
      }
    })
  },
  setCurrentConnectionId: (state: DataExplorerState, action: PayloadAction<string>) => {
    state.currentConnectionId = action.payload
  },
  deleteExpandedNode: (state: DataExplorerState, action: PayloadAction<string>) => {
    state.expandedNodes = state.expandedNodes.filter((n) => n !== action.payload)
  },
  addToExpandedNodes: (state: DataExplorerState, action: PayloadAction<string>) => {
    state.expandedNodes.push(action.payload)
  },
  setExpandedNodes: (state: DataExplorerState, action: PayloadAction<string[]>) => {
    state.expandedNodes = action.payload
  },
  setCurrentSelectedNode: (state: DataExplorerState, action: PayloadAction<string>) => {
    state.currentSelectedNode = action.payload
  },
  setCurrentSelectedTreeItemId: (state: DataExplorerState, action: PayloadAction<string>) => {
    state.currentSelectedTreeItemId = action.payload
  },
  setCurrentTableDataPreview: (state: DataExplorerState, action: PayloadAction<DataTablePreview | null>) => {
    state.currentTableDataPreview = action.payload
  },
  setCurrentTableDataPreviewStatus: (state: DataExplorerState, action: PayloadAction<RequestStatus>) => {
    state.currentTableDataPreviewStatus = action.payload
  },
  setCurrentTableDataPreviewError: (state: DataExplorerState, action: PayloadAction<ApiError | null>) => {
    state.currentTableDataPreviewError = action.payload
  },
  setCheckWritePermissionsStatus: (state: DataExplorerState, action: PayloadAction<RequestStatus>) => {
    state.checkWritePermissionStatus = action.payload
  },
  setCheckWritePermissionsError: (state: DataExplorerState, action: PayloadAction<ApiError | undefined>) => {
    state.checkWritePermissionError = action.payload
  },
  setDataExplorerSearching: (state: DataExplorerState, action: PayloadAction<boolean>) => {
    state.dataExplorerSearching = action.payload
  },
  deleteResource: (state: DataExplorerState, action: PayloadAction<{ connectionId: string; path: string }>) => {
    const dataExplorerTree = state.dataExplorerTree
    const pathSplit = action.payload.path.split('.')
    Object.keys(dataExplorerTree).map((providerKey: string) => {
      let deleteObjectTarget = state.dataExplorerTree[providerKey][action.payload.connectionId]?.children
      if (deleteObjectTarget) {
        for (const part of pathSplit) {
          if (deleteObjectTarget && deleteObjectTarget[part]?.children) {
            deleteObjectTarget = deleteObjectTarget[part]?.children
          } else if (deleteObjectTarget && isResource(deleteObjectTarget[part])) {
            delete deleteObjectTarget[part]
            break
          }
        }
      }
    })
  },
  deleteResourceStatus: (state: DataExplorerState, action: PayloadAction<RequestStatus>) => {
    state.deleteResourceStatus = action.payload
  },
  deleteResourceError: (state: DataExplorerState, action: PayloadAction<ApiError | null>) => {
    state.deleteResourceError = action.payload
  },
  setCurrentTableForEnrich: (state: DataExplorerState, action: PayloadAction<DataExplorerTable | null>) => {
    state.currentTableForEnrich = action.payload
  },
  setCurrentTableForEnrichStatus: (state: DataExplorerState, action: PayloadAction<RequestStatus>) => {
    state.currentTableForEnrichStatus = action.payload
  },
  setFolderView: (state: DataExplorerState, action: PayloadAction<'table' | 'grid'>) => {
    state.dataExplorerFolderView = action.payload
  },
  setDataExplorerSearchNotFound: (state: DataExplorerState, action: PayloadAction<boolean>) => {
    state.dataExplorerSearchNotFound = action.payload
  },
  resetDataExplorerTree: (state: DataExplorerState) => {
    state.dataExplorerTree = {}
  }
}

interface IsDataExplorerTreeNodeOpenProps {
  provider: ProviderType
  connectionId: Connection['id']
  items: Array<DataExplorerTreeItem['id']>
}
export const isItemLoadedInDataExplorerTree =
  ({ provider, connectionId, items }: IsDataExplorerTreeNodeOpenProps) =>
  (state: RootState) => {
    let node = state.dataExplorer.dataExplorerTree[provider][connectionId]

    for (const item of items) {
      if (node && node.children) {
        node = node.children[item]
      } else {
        return false
      }
    }

    return node && node.children !== undefined
  }

export const dataExplorerSlice = createSlice({
  name,
  initialState,
  reducers
})

export const {
  setExpandedTree,
  updateDataExplorerTreeConnection,
  setDataExplorerTree,
  setDataExplorerTreeStatus,
  setDataExplorerTreeError,
  setDeleteConnectionFromDataTree,
  setDataExplorerTreeLoadingNode,
  setDataExplorerLoadingNextPage,
  setDataExplorerSearching,
  setDataExplorerNodeNotFound,
  deleteDataExplorerTreeLoadingNode,
  clearDataExplorerTreeLoadingErrors,
  setCurrentRaster,
  setCurrentRasterStatus,
  setCurrentRasterError,
  setCurrentTileset,
  setCurrentTilesetStatus,
  setCurrentTilesetError,
  setCurrentTilesetStats,
  setCurrentTilesetStatsStatus,
  setCurrentTilesetStatsError,
  setCurrentTable,
  setCurrentTableStatus,
  setCurrentTableError,
  setCurrentTableResourceStatsStatus,
  setCurrentTableDataPreview,
  setCurrentTableDataPreviewStatus,
  setCurrentTableDataPreviewError,
  setCurrentConnectionId,
  setExpandedNodes,
  addToExpandedNodes,
  deleteExpandedNode,
  setCurrentSelectedNode,
  setCurrentSelectedTreeItemId,
  setCheckWritePermissionsStatus,
  setCheckWritePermissionsError,
  deleteResource,
  deleteResourceStatus,
  deleteResourceError,
  setCurrentTableForEnrich,
  setCurrentTableForEnrichStatus,
  setFolderView,
  setDataExplorerSearchNotFound,
  resetDataExplorerTree
} = dataExplorerSlice.actions

export const {
  fetchDataTreeResourceRequest,
  fetchDataTreeResourceByLink,
  fetchDataExplorerObjectRequest,
  fetchTilesetStatsRequest,
  fetchResourcesFromPath,
  fetchTablePreviewRequest,
  getTableStatsRequest,
  getSubscriptionInfoRequest,
  deleteResourceRequest,
  reloadDataTreeItemRequest,
  getSubscriptionForEnrichRequest,
  getTableStatsForEnrichRequest,
  resetTableForEnrichRequest,
  checkTableResourceStatsRequest
} = asyncActions

function findAndGetClosestUrlPath(dataExplorerTree, provider, connectionId, items) {
  let node = { ...dataExplorerTree[provider][connectionId] }
  let response = ''
  for (const item of items) {
    if (node && node.children) {
      node = node.children[item]
      if (node) {
        item && (response += `${item}.`)
      }
    }
  }

  return response.slice(0, -1)
}

export const getClosestExistingUrl = createSelector(
  (state: DataExplorerState) => state,
  (_: unknown, connectionId: string, currentPath: string) => {
    return { connectionId, currentPath }
  },
  (state, args) => {
    const { dataExplorerTree, deleteResourceStatus } = state
    const { connectionId, currentPath } = args
    if (deleteResourceStatus !== RequestStatus.Fulfilled) return

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let response: any = false
    Object.keys(dataExplorerTree).map((providerKey: string) => {
      const connectionIndex = Object.keys(dataExplorerTree[providerKey]).indexOf(connectionId)
      if (connectionIndex !== -1) {
        const nextUrl = findAndGetClosestUrlPath(dataExplorerTree, providerKey, connectionId, currentPath.split('.'))
        response = nextUrl !== currentPath ? nextUrl : false
      }
    })
    return response
  }
)

export default dataExplorerSlice.reducer
